import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  MoreVertical,
  CheckCheck,
  Trash2,
  Pencil,
  Clock,
  CopyCheck,
  RotateCcw,
  X,
} from 'lucide-react';
import { Button } from '../shared/ui/Button';
import { Skeleton } from '../shared/ui/Skeleton';
import { CopyButton } from '../shared/ui/CopyButton';
import { Badge } from '../shared/ui/Badge';
import { Checkbox } from '../shared/ui/Checkbox';
import { ConfirmModal } from '../shared/ui/ConfirmModal';
import { Modal, ModalActions } from '../shared/ui/Modal';
import { InfoBanner } from '../shared/ui/InfoBanner';
import { Textarea } from '../shared/ui/Textarea';
import { toast } from '../shared/ui/toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shared/ui/DropdownMenu';
import {
  useActivateMailbox,
  useDeleteMailbox,
  useExtendMailbox,
  useMailbox,
  useUpdateMailbox,
} from '../features/mailbox/api';
import { useRetention } from '../features/mailbox/useRetention';
import { useForwardingHidden } from '../features/mailbox/useForwardingHidden';
import { useForwardingReady } from '../features/forwarding/useForwardingReady';
import {
  useDeleteAllMessages,
  useDeleteMessages,
  useMarkAllRead,
  useMarkRead,
  useMarkReadMany,
  useMessages,
  usePermanentDeleteMessages,
  useRestoreMessages,
  useTrashMessages,
} from '../features/message/api';
import { MessageRow } from '../features/message/components/MessageRow';
import { isMailboxActive, mailboxStatusView } from '../features/mailbox/lib';
import { useNow } from '../shared/lib/useNow';

type Tab = 'inbox' | 'trash';

export function MailboxDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mailboxId } = useParams({ from: '/mailbox/$mailboxId' });

  const mailboxQuery = useMailbox(mailboxId);
  const messagesQuery = useMessages(mailboxId);
  const [tab, setTab] = useState<Tab>('inbox');
  const trashQuery = useTrashMessages(mailboxId, tab === 'trash');

  const markRead = useMarkRead(mailboxId);
  const markAllRead = useMarkAllRead(mailboxId);
  const deleteAll = useDeleteAllMessages(mailboxId);
  const deleteMessages = useDeleteMessages(mailboxId);
  const permanentDeleteMessages = usePermanentDeleteMessages(mailboxId);
  const markReadMany = useMarkReadMany(mailboxId);
  const restoreMessages = useRestoreMessages(mailboxId);
  const updateMailbox = useUpdateMailbox();
  const deleteMailbox = useDeleteMailbox();
  const activate = useActivateMailbox();
  const extend = useExtendMailbox();
  const retention = useRetention();
  const forwardingHidden = useForwardingHidden();
  const forwardingReady = useForwardingReady();
  const now = useNow();

  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [confirmDeleteMailbox, setConfirmDeleteMailbox] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [trashHintVisible, setTrashHintVisible] = useState(true);

  const mailbox = mailboxQuery.data;
  const status = mailbox ? mailboxStatusView(mailbox, now) : null;
  const active = mailbox ? isMailboxActive(mailbox, now) : false;

  const activeQuery = tab === 'inbox' ? messagesQuery : trashQuery;
  const messages = useMemo(() => activeQuery.data?.pages.flatMap((p) => p.items) ?? [], [activeQuery.data]);

  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const switchTab = (next: Tab) => {
    setTab(next);
    exitSelect();
  };

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (checked) s.add(id);
      else s.delete(id);
      return s;
    });
  };

  const allChecked = messages.length > 0 && selected.size >= messages.length;
  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(messages.map((m) => m.id)) : new Set());
  };

  const openMessage = (id: string) => {
    markRead.mutate(id);
    void navigate({
      to: '/mailbox/$mailboxId/message/$messageId',
      params: { mailboxId, messageId: id },
    });
  };

  const bulkPending =
    deleteMessages.isPending ||
    permanentDeleteMessages.isPending ||
    markReadMany.isPending ||
    restoreMessages.isPending;

  const runBulkDelete = () => {
    const ids = [...selected];
    deleteMessages.mutate(ids, {
      onSuccess: (r) => {
        toast.success(t('message.toasts.bulkDeleted', { count: r.ok }));
        exitSelect();
      },
    });
  };
  const runBulkPurge = () => {
    const ids = [...selected];
    permanentDeleteMessages.mutate(ids, {
      onSuccess: (r) => {
        toast.success(t('message.toasts.bulkPurged', { count: r.ok }));
        setConfirmPurge(false);
        exitSelect();
      },
    });
  };
  const runBulkRead = () => {
    const ids = [...selected];
    markReadMany.mutate(ids, {
      onSuccess: (r) => {
        toast.success(t('message.toasts.bulkRead', { count: r.ok }));
        exitSelect();
      },
    });
  };
  const runBulkRestore = () => {
    const ids = [...selected];
    restoreMessages.mutate(ids, {
      onSuccess: (r) => {
        toast.success(t('message.toasts.bulkRestored', { count: r.ok }));
        exitSelect();
      },
    });
  };

  // При включённой переадресации бэкенд не отдаёт письма — объясняем пустой список (promt-81 §Q5).
  const messagesForwarded = forwardingHidden && mailbox?.forwardingEnabled === true;
  const emptyTitle = messagesForwarded
    ? t('message.forwardedEmptyTitle')
    : tab === 'trash'
      ? t('message.trashEmptyTitle')
      : t('message.emptyTitle');
  const emptySubtitle = messagesForwarded
    ? t('message.forwardedEmptySubtitle')
    : tab === 'trash'
      ? t('message.trashEmptySubtitle')
      : t('message.emptySubtitle');

  return (
    // Нижняя панель выбора на мобильных перекрывает контент — компенсируем отступом.
    <div className={`space-y-4 ${selectMode ? 'pb-20 sm:pb-0' : ''}`}>
      <button
        type="button"
        onClick={() => void navigate({ to: '/' })}
        className="flex items-center gap-2 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="h-4 w-4" /> {t('app.back')}
      </button>

      {mailboxQuery.isLoading || !mailbox ? (
        <Skeleton className="h-16 w-full rounded-2xl" />
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          {/* Левая колонка: адрес + описание + бейдж переадресации (promt-74 §7c/§7f). */}
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-2xl font-bold text-fg">{mailbox.address.full}</h1>
              <CopyButton value={mailbox.address.full} />
            </div>
            <button
              type="button"
              onClick={() => {
                setDescDraft(mailbox.description ?? '');
                setDescOpen(true);
              }}
              className="mt-1 flex max-w-full items-center gap-1.5 text-sm text-fg-muted hover:text-fg"
            >
              <span className="truncate">{mailbox.description ?? t('mailbox.description.add')}</span>
              <Pencil className="h-3.5 w-3.5 shrink-0" />
            </button>
            {mailbox.forwardingEnabled && (
              <div className="mt-2">
                <Badge tone="forwarding" dot>
                  {t('status.forwarding')}
                </Badge>
              </div>
            )}
          </div>

          {/* Правая колонка: метка времени активности + действие; на мобилке — во всю ширину. */}
          <div className="flex shrink-0 flex-col gap-3 sm:items-end">
            {status && (
              <span className="flex items-center gap-1.5 text-sm text-fg-muted">
                <Clock className="h-4 w-4 shrink-0" />
                {t(status.key, { time: status.time })}
              </span>
            )}
            <div className="flex items-center gap-2">
              {active ? (
                <Button
                  className="flex-1 sm:flex-none"
                  variant="secondary"
                  loading={extend.isPending}
                  onClick={() => {
                    extend.mutate(mailbox.id, {
                      onSuccess: () => toast.success(t('mailbox.toasts.extended')),
                    });
                  }}
                >
                  {t('message.extendMailbox')}
                </Button>
              ) : (
                <Button
                  className="flex-1 sm:flex-none"
                  loading={activate.isPending}
                  onClick={() => {
                    activate.mutate(mailbox.id, {
                      onSuccess: () => toast.success(t('mailbox.toasts.activated')),
                    });
                  }}
                >
                  {t('message.activateMailbox')}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Меню"
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand hover:bg-brand-softhover"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                {/* Меню внутри ящика — без иконок (макет РТК image006). */}
                <DropdownMenuContent>
                  {forwardingReady ? (
                    <DropdownMenuItem
                      onSelect={() => {
                        updateMailbox.mutate(
                          { id: mailbox.id, forwardingEnabled: !mailbox.forwardingEnabled },
                          {
                            onSuccess: () =>
                              toast.success(
                                mailbox.forwardingEnabled
                                  ? t('mailbox.toasts.forwardingOff')
                                  : t('mailbox.toasts.forwardingOn')
                              ),
                          }
                        );
                      }}
                    >
                      {mailbox.forwardingEnabled
                        ? t('message.menu.disableForwarding')
                        : t('message.menu.enableForwarding')}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={() => void navigate({ to: '/forwarding' })}>
                      {t('message.menu.setupForwarding')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onSelect={() => {
                      markAllRead.mutate(undefined, {
                        onSuccess: () => toast.success(t('message.toasts.markedAllRead')),
                      });
                    }}
                  >
                    {t('message.menu.markAllRead')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    destructive
                    onSelect={() => {
                      setConfirmDeleteAll(true);
                    }}
                  >
                    {t('message.menu.deleteAll')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    destructive
                    onSelect={() => {
                      setConfirmDeleteMailbox(true);
                    }}
                  >
                    {t('message.menu.deleteMailbox')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      )}

      {/* Фильтры вкладок + режим выбора (promt-74 §7f). */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {(['inbox', 'trash'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                switchTab(k);
              }}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                tab === k ? 'bg-brand text-white' : 'bg-page text-fg-muted hover:text-fg'
              }`}
            >
              {t(`message.tabs.${k}`)}
            </button>
          ))}
        </div>
        {selectMode ? (
          <button
            type="button"
            onClick={exitSelect}
            className="text-sm font-medium text-fg-muted hover:text-fg"
          >
            {t('message.selectCancel')}
          </button>
        ) : (
          // Выбирать нечего, пока в ящике нет писем (правка Ирины п.10).
          messages.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setSelectMode(true);
              }}
              className="flex items-center gap-1.5 text-sm font-medium text-fg-muted hover:text-fg"
            >
              <CopyCheck className="h-4 w-4" /> {t('message.select')}
            </button>
          )
        )}
      </div>

      {tab === 'trash' && trashHintVisible && (
        <InfoBanner
          onDismiss={() => {
            setTrashHintVisible(false);
          }}
        >
          {t('message.trashRetentionHint', { count: retention.messageTrashDays })}
        </InfoBanner>
      )}

      {/* Панель массовых действий: на десктопе строкой, на мобильных — нижней панелью (макет РТК). */}
      {selectMode && messages.length > 0 && (
        <>
          <div className="hidden flex-wrap items-center gap-x-4 gap-y-2 sm:flex">
            <label className="flex items-center gap-2 text-sm font-medium text-fg">
              <Checkbox
                checked={allChecked ? true : selected.size > 0 ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
                aria-label={t('message.selectAll')}
              />
              {t('message.selectAll')}
            </label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                disabled={selected.size === 0 || bulkPending}
                onClick={
                  tab === 'trash'
                    ? () => {
                        setConfirmPurge(true);
                      }
                    : runBulkDelete
                }
                className="flex items-center gap-1.5 text-sm font-medium text-danger hover:opacity-80 disabled:text-fg-muted disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" />{' '}
                {tab === 'trash' ? t('message.bulk.deleteForever') : t('message.bulk.delete')}
              </button>
              {tab === 'trash' ? (
                <button
                  type="button"
                  disabled={selected.size === 0 || bulkPending}
                  onClick={runBulkRestore}
                  className="flex items-center gap-1.5 text-sm font-medium text-fg-muted hover:text-fg disabled:opacity-40"
                >
                  <RotateCcw className="h-4 w-4" /> {t('message.bulk.restore')}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={selected.size === 0 || bulkPending}
                  onClick={runBulkRead}
                  className="flex items-center gap-1.5 text-sm font-medium text-fg-muted hover:text-fg disabled:opacity-40"
                >
                  <CheckCheck className="h-4 w-4" /> {t('message.bulk.read')}
                </button>
              )}
            </div>
          </div>

          <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-border bg-surface px-4 py-3 shadow-pop sm:hidden">
            <button
              type="button"
              aria-label={tab === 'trash' ? t('message.bulk.deleteForever') : t('message.bulk.delete')}
              disabled={selected.size === 0 || bulkPending}
              onClick={
                tab === 'trash'
                  ? () => {
                      setConfirmPurge(true);
                    }
                  : runBulkDelete
              }
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-page text-danger disabled:text-fg-muted disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={tab === 'trash' ? t('message.bulk.restore') : t('message.bulk.read')}
              disabled={selected.size === 0 || bulkPending}
              onClick={tab === 'trash' ? runBulkRestore : runBulkRead}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-page text-fg-muted disabled:opacity-40"
            >
              {tab === 'trash' ? <RotateCcw className="h-4 w-4" /> : <CheckCheck className="h-4 w-4" />}
            </button>
            <label className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-fg">
              <Checkbox
                checked={allChecked ? true : selected.size > 0 ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
                aria-label={t('message.selectAll')}
              />
              <span className="truncate">{t('message.selectAll')}</span>
            </label>
            <button
              type="button"
              aria-label={t('message.selectCancel')}
              onClick={exitSelect}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-fg-muted hover:text-fg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </>
      )}

      <div className="overflow-hidden rounded-2xl bg-surface shadow-card">
        {activeQuery.isLoading ? (
          <div className="space-y-3 p-5">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : messages.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <h2 className="text-xl font-bold text-fg">{emptyTitle}</h2>
            <p className="mt-2 text-fg-muted">{emptySubtitle}</p>
          </div>
        ) : (
          messages.map((m) => (
            <MessageRow
              key={m.id}
              message={m}
              onOpen={openMessage}
              selectable={selectMode}
              selected={selected.has(m.id)}
              onToggle={toggle}
            />
          ))
        )}
      </div>

      {activeQuery.hasNextPage && (
        <div className="text-center">
          <Button
            variant="secondary"
            onClick={() => void activeQuery.fetchNextPage()}
            loading={activeQuery.isFetchingNextPage}
          >
            {t('common.showMore')}
          </Button>
        </div>
      )}

      <ConfirmModal
        open={confirmDeleteAll}
        onOpenChange={setConfirmDeleteAll}
        title={t('message.menu.deleteAll')}
        confirmLabel={t('common.delete')}
        loading={deleteAll.isPending}
        onConfirm={() => {
          deleteAll.mutate(undefined, {
            onSuccess: () => {
              toast.success(t('message.toasts.deletedAll'));
              setConfirmDeleteAll(false);
            },
          });
        }}
      />
      <ConfirmModal
        open={confirmPurge}
        onOpenChange={setConfirmPurge}
        title={t('message.purge.title', { count: selected.size })}
        description={t('message.purge.subtitle')}
        confirmLabel={t('message.purge.confirm')}
        loading={permanentDeleteMessages.isPending}
        onConfirm={runBulkPurge}
      />
      <ConfirmModal
        open={confirmDeleteMailbox}
        onOpenChange={setConfirmDeleteMailbox}
        title={t('mailbox.delete.title', { address: mailbox?.address.full ?? '' })}
        description={t('mailbox.delete.subtitle', { count: retention.mailboxTrashDays })}
        confirmLabel={t('mailbox.delete.confirm')}
        loading={deleteMailbox.isPending}
        onConfirm={() => {
          if (!mailbox) return;
          deleteMailbox.mutate(mailbox.id, {
            onSuccess: () => {
              toast.success(t('mailbox.toasts.deleted'));
              void navigate({ to: '/' });
            },
          });
        }}
      />

      <Modal open={descOpen} onOpenChange={setDescOpen} title={t('mailbox.description.title')}>
        <Textarea
          label={t('mailbox.description.placeholder')}
          value={descDraft}
          counterMax={200}
          onChange={(e) => {
            setDescDraft(e.target.value);
          }}
          onClear={() => {
            setDescDraft('');
          }}
        />
        <ModalActions>
          <Button
            loading={updateMailbox.isPending}
            onClick={() => {
              if (!mailbox) return;
              updateMailbox.mutate(
                { id: mailbox.id, description: descDraft.trim() },
                {
                  onSuccess: () => {
                    setDescOpen(false);
                  },
                }
              );
            }}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setDescOpen(false);
            }}
          >
            {t('common.cancel')}
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
}
